import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAutoRetryOptions {
  /** Maximum number of automatic retries */
  maxRetries?: number;
  /** Base delay between retries in ms (will use exponential backoff) */
  baseDelay?: number;
  /** Maximum delay between retries in ms */
  maxDelay?: number;
  /** Whether the query is currently in error state */
  isError: boolean;
  /** Function to call to retry */
  onRetry: () => void | Promise<void>;
  /** Whether the operation is currently loading/fetching */
  isFetching?: boolean;
}

interface UseAutoRetryResult {
  /** Number of retries attempted */
  retryCount: number;
  /** Seconds until next automatic retry (null if not retrying) */
  countdownSeconds: number | null;
  /** Whether max retries have been exhausted */
  isExhausted: boolean;
  /** Manual retry function that resets the retry count */
  manualRetry: () => void;
  /** Reset the retry state */
  reset: () => void;
}

/**
 * Hook for automatic retry with exponential backoff
 * Provides countdown timer and retry state for UX feedback
 */
export function useAutoRetry({
  maxRetries = 3,
  baseDelay = 3000,
  maxDelay = 15000,
  isError,
  onRetry,
  isFetching = false,
}: UseAutoRetryOptions): UseAutoRetryResult {
  const [retryCount, setRetryCount] = useState(0);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevErrorRef = useRef(isError);

  // Calculate delay with exponential backoff and jitter
  const getDelayMs = useCallback((attempt: number) => {
    // Exponential backoff: baseDelay * 2^attempt + random jitter
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 1000; // 0-1s jitter
    return Math.min(exponentialDelay + jitter, maxDelay);
  }, [baseDelay, maxDelay]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Reset state
  const reset = useCallback(() => {
    clearTimers();
    setRetryCount(0);
    setCountdownSeconds(null);
  }, [clearTimers]);

  // Manual retry resets everything
  const manualRetry = useCallback(() => {
    reset();
    onRetry();
  }, [reset, onRetry]);

  // Handle error state changes
  useEffect(() => {
    // If error resolved, reset
    if (!isError && prevErrorRef.current) {
      reset();
    }
    prevErrorRef.current = isError;
  }, [isError, reset]);

  // Start countdown when error occurs
  useEffect(() => {
    // Don't start if:
    // - No error
    // - Already fetching (retry in progress)
    // - Exhausted retries
    // - Already counting down
    if (!isError || isFetching || retryCount >= maxRetries || countdownSeconds !== null) {
      return;
    }

    const delayMs = getDelayMs(retryCount);
    const delaySeconds = Math.ceil(delayMs / 1000);
    
    setCountdownSeconds(delaySeconds);
    
    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev === null || prev <= 1) {
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    // Actual retry timeout
    retryTimeoutRef.current = setTimeout(() => {
      clearTimers();
      setRetryCount(prev => prev + 1);
      onRetry();
    }, delayMs);

    return clearTimers;
  }, [isError, isFetching, retryCount, maxRetries, countdownSeconds, getDelayMs, onRetry, clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return clearTimers;
  }, [clearTimers]);

  return {
    retryCount,
    countdownSeconds,
    isExhausted: retryCount >= maxRetries && isError,
    manualRetry,
    reset,
  };
}
